import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { StatCardsSkeleton, ListSkeleton } from "@/components/PageSkeleton";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  ClipboardList, CheckCircle2, Lock, Star, ChevronRight, Send,
  Clock, AlertCircle,
} from "lucide-react";
import {
  Survey, SurveyDetail, SurveyAnswerPayload,
  apiGetEmployeeSurveys, apiGetSurvey, apiSubmitSurveyResponse, getTenantSlug,
} from "@/lib/api";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDueDate(date: string) {
  const d = new Date(date);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / 86400000);
  if (diffDays < 0) return { label: "Overdue", urgent: true };
  if (diffDays === 0) return { label: "Due today", urgent: true };
  if (diffDays === 1) return { label: "Due tomorrow", urgent: true };
  if (diffDays <= 7) return { label: `Due in ${diffDays} days`, urgent: true };
  return { label: `Due ${d.toLocaleDateString([], { month: "short", day: "numeric" })}`, urgent: false };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function MySurveys() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Surveys data ────────────────────────────────────────────────────────────
  const { data: surveys = [], isLoading } = useQuery<Survey[]>({
    queryKey: ["my-surveys", getTenantSlug()],
    queryFn: apiGetEmployeeSurveys,
  });

  // ── Fill-survey dialog state ─────────────────────────────────────────────────
  const [fillOpen, setFillOpen] = useState(false);
  const [activeSurvey, setActiveSurvey] = useState<SurveyDetail | null>(null);
  const [answers, setAnswers] = useState<
    Record<number, { optionId?: number; optionIds?: number[]; textAnswer?: string; ratingValue?: number }>
  >({});
  const [submitting, setSubmitting] = useState(false);

  const openSurvey = async (s: Survey) => {
    if (s.hasResponded) return;
    try {
      const full = await apiGetSurvey(s.id, "mine");
      setActiveSurvey(full);
      setAnswers({});
      setFillOpen(true);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // Deep-link: auto-open survey when ?survey=ID is in the URL
  useEffect(() => {
    const surveyId = searchParams.get("survey");
    if (!surveyId || surveys.length === 0) return;
    const target = surveys.find((s) => s.id === Number(surveyId));
    if (target && !target.hasResponded) {
      openSurvey(target);
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("survey");
      return next;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveys, searchParams]);

  const toggleOption = (qId: number, optId: number, multi: boolean) => {
    setAnswers((prev) => {
      const cur = prev[qId] || {};
      if (multi) {
        const ids = cur.optionIds || [];
        return { ...prev, [qId]: { ...cur, optionIds: ids.includes(optId) ? ids.filter((x) => x !== optId) : [...ids, optId] } };
      }
      return { ...prev, [qId]: { ...cur, optionId: optId } };
    });
  };

  const handleSubmit = async () => {
    if (!activeSurvey) return;
    for (const q of activeSurvey.questions) {
      if (!q.isRequired) continue;
      const a = answers[q.id];
      if (!a) { toast({ title: t("surveys.answerRequired", { q: q.questionText }), variant: "destructive" }); return; }
      if (q.questionType === "single_choice" && !a.optionId) { toast({ title: t("surveys.answerRequired", { q: q.questionText }), variant: "destructive" }); return; }
      if (q.questionType === "multiple_choice" && (!a.optionIds || a.optionIds.length === 0)) { toast({ title: t("surveys.answerRequired", { q: q.questionText }), variant: "destructive" }); return; }
      if (q.questionType === "text" && !a.textAnswer?.trim()) { toast({ title: t("surveys.answerRequired", { q: q.questionText }), variant: "destructive" }); return; }
      if (q.questionType === "rating" && !a.ratingValue) { toast({ title: t("surveys.answerRequired", { q: q.questionText }), variant: "destructive" }); return; }
    }
    setSubmitting(true);
    try {
      const payload: SurveyAnswerPayload[] = [];
      for (const q of activeSurvey.questions) {
        const a = answers[q.id];
        if (!a) continue;
        if (q.questionType === "single_choice" && a.optionId) payload.push({ questionId: q.id, optionId: a.optionId });
        else if (q.questionType === "multiple_choice" && a.optionIds?.length) payload.push({ questionId: q.id, optionIds: a.optionIds });
        else if (q.questionType === "text" && a.textAnswer) payload.push({ questionId: q.id, textAnswer: a.textAnswer });
        else if (q.questionType === "rating" && a.ratingValue) payload.push({ questionId: q.id, ratingValue: a.ratingValue });
      }
      await apiSubmitSurveyResponse(activeSurvey.id, payload);
      toast({ title: t("surveys.submitted") });
      setFillOpen(false);
      qc.invalidateQueries({ queryKey: ["my-surveys", getTenantSlug()] });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const pending   = surveys.filter((s) => !s.hasResponded);
  const completed = surveys.filter((s) =>  s.hasResponded);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="space-y-6 w-full">

        {/* Page header */}
        <PageHeader
          title={t("surveys.mySurveys")}
          subtitle={t("surveys.mySurveysSubtitle")}
          icon={ClipboardList}
          iconColor="text-violet-600"
          iconBg="bg-violet-500/10"
        />

        {/* Summary strip */}
        {!isLoading && surveys.length > 0 && (
          <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
            <div className="grid grid-cols-3 divide-x divide-border/60">
              <div className="flex items-center gap-3.5 px-5 py-4">
                <div className="h-9 w-9 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
                  <ClipboardList className="h-4 w-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold text-foreground leading-none">{surveys.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Total surveys</p>
                </div>
              </div>
              <div className="flex items-center gap-3.5 px-5 py-4">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                  <Clock className="h-4 w-4 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold text-foreground leading-none">{pending.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Awaiting response</p>
                </div>
              </div>
              <div className="flex items-center gap-3.5 px-5 py-4">
                <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold text-foreground leading-none">{completed.length}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Completed</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="space-y-6">
            <StatCardsSkeleton count={3} />
            <ListSkeleton rows={4} />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && surveys.length === 0 && (
          <div className="rounded-2xl border border-border/60 bg-card shadow-sm py-16 text-center">
            <div className="h-16 w-16 rounded-2xl bg-violet-500/10 flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="h-8 w-8 text-violet-400" />
            </div>
            <p className="font-semibold text-foreground mb-1">No surveys yet</p>
            <p className="text-sm text-muted-foreground">{t("surveys.noSurveysAvailable")}</p>
          </div>
        )}

        {/* Pending surveys */}
        {!isLoading && pending.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-amber-500 inline-block" />
              {t("surveys.pendingSurveys")}
              <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-amber-500/15 text-amber-600 text-[11px] font-bold">
                {pending.length}
              </span>
            </h2>

            <div className="space-y-2">
              {pending.map((s) => {
                const due = s.endDate ? formatDueDate(s.endDate) : null;
                return (
                  <button
                    key={s.id}
                    className="w-full text-left group"
                    onClick={() => openSurvey(s)}
                  >
                    <div className="rounded-xl border border-border/60 bg-card shadow-sm hover:shadow-md hover:border-violet-200 dark:hover:border-violet-500/30 hover:-translate-y-0.5 transition-all p-4 flex items-center gap-4">
                      {/* Icon */}
                      <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
                        <ClipboardList className="h-5 w-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className="font-semibold text-sm text-foreground truncate">{s.title}</p>
                          {s.isAnonymous && (
                            <Badge variant="outline" className="text-[10px] h-5 gap-1 shrink-0 border-slate-300 text-slate-500">
                              <Lock className="h-3 w-3" />
                              {t("surveys.anonymous")}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2.5 text-xs text-muted-foreground flex-wrap">
                          <span>{s.questionCount} {t("surveys.questions")}</span>
                          {due && (
                            <>
                              <span className="text-border/60">·</span>
                              <span className={`flex items-center gap-1 ${due.urgent ? "text-amber-600 font-medium" : ""}`}>
                                {due.urgent && <AlertCircle className="h-3 w-3" />}
                                {due.label}
                              </span>
                            </>
                          )}
                          {s.description && (
                            <>
                              <span className="text-border/60">·</span>
                              <span className="truncate max-w-[180px]">{s.description}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs font-medium text-violet-600 dark:text-violet-400 hidden sm:block">
                          Take survey
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Completed surveys */}
        {!isLoading && completed.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" />
              {t("surveys.completedSurveys")}
              <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-emerald-500/15 text-emerald-600 text-[11px] font-bold">
                {completed.length}
              </span>
            </h2>

            <div className="space-y-2">
              {completed.map((s) => (
                <div
                  key={s.id}
                  className="rounded-xl border border-border/40 bg-card/60 p-4 flex items-center gap-4 opacity-70"
                >
                  {/* Icon */}
                  <div className="h-11 w-11 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 shrink-0">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground/80 truncate">{s.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("surveys.alreadyCompleted")}
                      {s.questionCount ? ` · ${s.questionCount} ${t("surveys.questions")}` : ""}
                    </p>
                  </div>

                  {/* Status badge */}
                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-200 dark:border-emerald-500/20 text-[10px] h-5 shrink-0">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Completed
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Fill Survey Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={fillOpen} onOpenChange={setFillOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
          {/* Dialog header */}
          <div className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-start gap-3 mb-1">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white shrink-0 mt-0.5">
                <ClipboardList className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogHeader>
                  <DialogTitle className="text-base leading-snug">{activeSurvey?.title}</DialogTitle>
                </DialogHeader>
                {activeSurvey?.description && (
                  <p className="text-sm text-muted-foreground mt-1">{activeSurvey.description}</p>
                )}
              </div>
            </div>
            {activeSurvey?.isAnonymous && (
              <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-xs text-emerald-800 dark:text-emerald-400">
                <Lock className="h-3.5 w-3.5 shrink-0" />
                {t("surveys.anonymousGuarantee")}
              </div>
            )}
          </div>

          {/* Questions */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
            <div className="space-y-6">
              {activeSurvey?.questions.map((q, idx) => {
                const a = answers[q.id] || {};
                return (
                  <div key={q.id} className="space-y-3">
                    <div className="flex items-start gap-2">
                      <span className="flex-shrink-0 h-5 w-5 rounded-full bg-violet-500/10 text-violet-700 dark:text-violet-400 flex items-center justify-center text-[11px] font-bold mt-0.5">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium leading-snug">
                          {q.questionText}
                          {q.isRequired && <span className="text-red-500 ml-1">*</span>}
                        </p>
                      </div>
                    </div>

                    {q.questionType === "single_choice" && (
                      <div className="space-y-2 pl-7">
                        {q.options.map((o) => (
                          <button
                            key={o.id}
                            className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm transition-all ${
                              a.optionId === o.id
                                ? "border-violet-300 dark:border-violet-500/40 bg-violet-50 dark:bg-violet-500/10 font-medium text-violet-900 dark:text-violet-200"
                                : "border-border/60 hover:bg-secondary/50"
                            }`}
                            onClick={() => toggleOption(q.id, o.id, false)}
                          >
                            <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 ${a.optionId === o.id ? "border-violet-500" : "border-muted-foreground/30"}`}>
                              {a.optionId === o.id && <div className="h-2 w-2 rounded-full bg-violet-500" />}
                            </div>
                            {o.optionText}
                          </button>
                        ))}
                      </div>
                    )}

                    {q.questionType === "multiple_choice" && (
                      <div className="space-y-2 pl-7">
                        {q.options.map((o) => {
                          const sel = (a.optionIds || []).includes(o.id);
                          return (
                            <button
                              key={o.id}
                              className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm transition-all ${
                                sel
                                  ? "border-violet-300 dark:border-violet-500/40 bg-violet-50 dark:bg-violet-500/10 font-medium text-violet-900 dark:text-violet-200"
                                  : "border-border/60 hover:bg-secondary/50"
                              }`}
                              onClick={() => toggleOption(q.id, o.id, true)}
                            >
                              <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${sel ? "bg-violet-500 border-violet-500 text-white" : "border-muted-foreground/30"}`}>
                                {sel && <CheckCircle2 className="h-3 w-3" />}
                              </div>
                              {o.optionText}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {q.questionType === "text" && (
                      <div className="pl-7">
                        <Textarea
                          placeholder={t("surveys.typeAnswer")}
                          value={a.textAnswer || ""}
                          onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: { ...prev[q.id], textAnswer: e.target.value } }))}
                          className="min-h-[80px]"
                        />
                      </div>
                    )}

                    {q.questionType === "rating" && (
                      <div className="flex gap-1.5 pl-7">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            className="p-1 transition-transform hover:scale-110"
                            onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: { ...prev[q.id], ratingValue: n } }))}
                          >
                            <Star className={`h-8 w-8 transition-colors ${n <= (a.ratingValue || 0) ? "text-amber-400 fill-amber-400" : "text-muted-foreground/20 hover:text-amber-200"}`} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Submit */}
          <div className="border-t px-6 py-4">
            <Button onClick={handleSubmit} disabled={submitting} className="w-full gap-2">
              <Send className="h-4 w-4" />
              {submitting ? t("surveys.submitting") : t("surveys.submitSurvey")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
